import { ReactNode, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Package, ChevronDown, ChevronRight,
  User as UserIcon, LogOut, Bell, Menu, Store, ShoppingBag,
  Wrench, BarChart3, Settings, AlertTriangle, RefreshCw, Loader2, Calendar,
  MessageSquare, DollarSign, Megaphone, TrendingUp, ArrowUpCircle, BadgeCheck,
  Users as UsersIcon, Link2, ClipboardList,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { authApi } from "@/lib/api";
import { LayoutContextProvider, useLayoutCtx } from "./LayoutContext";

interface NavItem {
  label: string;
  href?: string;
  icon: typeof LayoutDashboard;
  children?: { label: string; href: string }[];
}

const navItems: NavItem[] = [
  { label: "Overview", href: "/merchant/dashboard", icon: LayoutDashboard },
  {
    label: "Storefront",
    icon: Store,
    children: [
      { label: "Products", href: "/merchant/products" },
      { label: "Services", href: "/merchant/services" },
    ],
  },
  // { label: "Events", href: "/merchant/events", icon: Calendar },
  { label: "Leads", href: "/merchant/leads", icon: MessageSquare },
  { label: "Sales", href: "/merchant/sales", icon: DollarSign },
  { label: "Promotions", href: "/merchant/promotions", icon: Megaphone },
  { label: "Orders", href: "/merchant/orders", icon: Package },
  { label: "Analytics", href: "/merchant/analytics", icon: BarChart3 },
  { label: "Investments", href: "/merchant/investments", icon: TrendingUp },
  { label: "Verification", href: "/merchant/verification", icon: BadgeCheck },
  { label: "Upgrade to Member", href: "/merchant/upgrade", icon: ArrowUpCircle },
  { label: "Profile", href: "/merchant/profile", icon: UserIcon },
  { label: "Settings", href: "/merchant/settings", icon: Settings },
];

const affiliateNavItem: NavItem = {
  label: "Affiliate",
  icon: UsersIcon,
  children: [
    { label: "Registrations", href: "/affiliate/registrations" },
    { label: "Link", href: "/affiliate/link" },
  ],
};

// Icon shown next to a dropdown child link (children only carry label + href).
const childIcon = (label: string) => {
  switch (label) {
    case "Products": return <ShoppingBag size={14} />;
    case "Registrations": return <ClipboardList size={14} />;
    case "Events": return <Calendar size={14} />;
    case "Link": return <Link2 size={14} />;
    default: return <Wrench size={14} />;
  }
};

export function MerchantLayoutChrome({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>("Storefront");
  const [subState, setSubState] = useState<{ loaded: boolean; expired: boolean; expires_at: string | null; package_name?: string }>({
    loaded: false,
    expired: false,
    expires_at: null,
  });
  const [renewLoading, setRenewLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== "vendor") {
      setSubState({ loaded: true, expired: false, expires_at: null });
      return;
    }
    authApi.paymentStatus()
      .then(res => setSubState({
        loaded: true,
        expired: res.is_expired,
        expires_at: res.expires_at,
        package_name: res.payment?.package_name,
      }))
      .catch(() => setSubState({ loaded: true, expired: false, expires_at: null }));
  }, [user]);

  const isActive = (href: string) => location.pathname === href;
  const isChildActive = (children?: { href: string }[]) => children?.some(c => isActive(c.href));

  const handleLogout = () => { logout(); navigate("/login"); };

  const handleRenew = async () => {
    setRenewLoading(true);
    try {
      await authApi.renew();
      const checkout = await authApi.checkout();
      window.location.href = checkout.redirect_url;
    } catch {
      setRenewLoading(false);
    }
  };

  const formatDate = (iso: string | null) => iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

  // Insert the Affiliate group just above Profile/Settings for affiliate vendors.
  const items: NavItem[] = user?.is_affiliate
    ? [...navItems.slice(0, -2), affiliateNavItem, ...navItems.slice(-2)]
    : navItems;

  const Sidebar = () => (
    <div className="flex flex-col h-full bg-navy">
      <div className="flex items-center px-5 h-16 border-b border-white/10 shrink-0">
        <span className="text-white font-semibold text-sm">Merchant Portal</span>
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {items.map((item) => {
          if (item.children) {
            const open = openDropdown === item.label;
            const childActive = isChildActive(item.children);
            return (
              <div key={item.label}>
                <button
                  onClick={() => setOpenDropdown(open ? null : item.label)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    childActive ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <item.icon size={18} className="shrink-0" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                <AnimatePresence>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="ml-3 mt-1 pl-4 border-l border-white/10 space-y-0.5 py-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            onClick={() => setMobileOpen(false)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-md text-[13px] transition-colors ${
                              isActive(child.href)
                                ? "bg-gold/15 text-gold font-medium"
                                : "text-white/60 hover:text-white hover:bg-white/5"
                            }`}
                          >
                            {childIcon(child.label)}
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          }
          return (
            <Link
              key={item.label}
              to={item.href!}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive(item.href!) ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"
              }`}
            >
              <item.icon size={18} className="shrink-0" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-white/10 p-3 shrink-0 space-y-1">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
            <span className="text-gold text-xs font-bold">{user?.name?.split(" ").map(n => n[0]).join("").slice(0, 2) || "M"}</span>
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.company_name || user?.name}</p>
            <p className="text-white/40 text-xs truncate">Merchant</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:text-red-400 hover:bg-white/5 transition-colors"
        >
          <LogOut size={18} />Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <aside className="hidden lg:flex w-64 shrink-0">
        <Sidebar />
      </aside>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/60 z-40"
            />
            <motion.aside
              initial={{ x: -260 }} animate={{ x: 0 }} exit={{ x: -260 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-64 z-50"
            >
              <Sidebar />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-6 shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden text-gray-500 hover:text-black">
              <Menu size={22} />
            </button>
            <h1 className="text-base font-semibold text-black">Merchant Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button className="text-gray-500 hover:text-black p-2"><Bell size={18} /></button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                <span className="text-gold text-xs font-bold">{user?.name?.charAt(0) || "M"}</span>
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-black leading-tight">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">Merchant</p>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          {subState.loaded && subState.expired ? (
            <div className="flex items-center justify-center min-h-[70vh]">
              <div className="max-w-md w-full bg-white border border-red-200 rounded-2xl shadow-sm p-8 text-center">
                <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                  <AlertTriangle className="text-red-600" size={32} />
                </div>
                <h2 className="text-xl font-bold text-black mb-2">Subscription Expired</h2>
                <p className="text-sm text-gray-500 mb-1">
                  Your merchant subscription expired on
                </p>
                <p className="text-base font-semibold text-red-600 mb-5 flex items-center justify-center gap-2">
                  <Calendar size={16} /> {formatDate(subState.expires_at)}
                </p>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Portal access is paused until you renew. Pay your monthly fee to restore your storefront, products, services and orders. If you believe this is a mistake, contact an administrator.
                </p>
                <button
                  onClick={handleRenew}
                  disabled={renewLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-gold text-gold-foreground font-semibold text-sm hover:bg-gold/90 disabled:opacity-70"
                >
                  {renewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw size={16} />}
                  Renew Subscription
                </button>
                <button
                  onClick={handleLogout}
                  className="mt-3 w-full text-xs text-gray-500 hover:text-gray-700"
                >
                  Sign out
                </button>
              </div>
            </div>
          ) : children}
        </main>
      </div>
    </div>
  );
}

export default function MerchantLayout({ children }: { children: ReactNode }) {
  const ctx = useLayoutCtx();
  if (ctx.mounted) return <>{children}</>;
  return (
    <LayoutContextProvider>
      <MerchantLayoutChrome>{children}</MerchantLayoutChrome>
    </LayoutContextProvider>
  );
}
